/**
 * CaseSummary — a shared, presentational "at-a-glance" stat strip for a case.
 *
 * Used by BOTH the real Case page (CaseDetail.tsx — investigation/evidence counts,
 * status) and the Fusion Inspector (nodes / links / deterministic vs scored).
 * Pure props in, no stores/hooks. Themed via design tokens.
 */
import type { ReactNode } from 'react';
import { clsx } from 'clsx';

export interface CaseStat {
  label: string;
  value: ReactNode;
  sub?: string;
  title?: string;
}

export interface CaseSummaryChip {
  label: string;
  color?: string;        // background; text auto-contrasts to white when set
}

export function CaseSummary({
  stats, chips, chipsLabel, className, layout = 'row',
}: {
  stats: CaseStat[];
  chips?: CaseSummaryChip[];
  chipsLabel?: string;
  className?: string;
  /** row = header strip; stack = inspector panel grid */
  layout?: 'row' | 'stack';
}) {
  const stack = layout === 'stack';

  return (
    <div
      className={clsx(
        stack ? 'grid grid-cols-2 gap-2.5' : 'flex items-center gap-x-5 gap-y-2 flex-wrap',
        className,
      )}
      data-testid={stack ? 'case-summary-stack' : 'case-summary-row'}
    >
      {chips && chips.length > 0 && (
        <div className={clsx('flex items-center gap-2', stack && 'col-span-2')}>
          {chipsLabel && <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{chipsLabel}</span>}
          <div className="flex items-center gap-1 flex-wrap">
            {chips.map((c, i) => (
              <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                style={c.color ? { background: c.color, color: '#fff' } : undefined}>
                {!c.color && <span className="text-muted-foreground">{c.label}</span>}
                {c.color && c.label}
              </span>
            ))}
          </div>
        </div>
      )}
      {stats.map((s, i) => (
        <div
          key={i}
          className={clsx(stack && 'rounded-md border border-border/70 bg-secondary/20 px-2.5 py-2')}
          title={s.title}
        >
          <span className={clsx(
            'font-semibold leading-none tabular-nums text-foreground',
            stack ? 'text-base' : 'text-[15px]',
          )}>
            {s.value}
          </span>
          <span className="block text-[9px] uppercase tracking-wide text-muted-foreground mt-1">
            {s.label}{s.sub ? ` · ${s.sub}` : ''}
          </span>
        </div>
      ))}
    </div>
  );
}
